import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ShoppingCart, Minus, Plus, Trash2, ExternalLink, Loader2, Truck, Sparkles } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { getDisplayTitle } from "@/lib/productAlias";

const FREE_SHIPPING_THRESHOLD = 1600;
const SHIPPING_COST = 150;

export const CartDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { items, isLoading, isSyncing, updateQuantity, removeItem, getCheckoutUrl, syncCart } = useCartStore();
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);
  const remainingForFreeShipping = Math.max(0, FREE_SHIPPING_THRESHOLD - totalPrice);
  const freeShippingProgress = Math.min(100, (totalPrice / FREE_SHIPPING_THRESHOLD) * 100);
  const hasFreeShipping = totalPrice >= FREE_SHIPPING_THRESHOLD;

  useEffect(() => { if (isOpen) syncCart(); }, [isOpen, syncCart]);

  const handleCheckout = () => {
    const checkoutUrl = getCheckoutUrl();
    if (checkoutUrl) {
      let finalUrl = checkoutUrl;
      if (hasFreeShipping) {
        try {
          const url = new URL(checkoutUrl);
          url.searchParams.set('discount', 'ENVIOGRATIS1600');
          finalUrl = url.toString();
        } catch { /* keep original */ }
      }
      window.open(finalUrl, '_blank');
      setIsOpen(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button className="relative p-2 rounded-full text-foreground/60 hover:text-primary hover:bg-primary/10 transition-all duration-200">
          <ShoppingCart size={20} />
          {totalItems > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center text-xs font-bold">
              {totalItems}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg flex flex-col h-full">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="font-display">Carrito</SheetTitle>
          <SheetDescription>
            {totalItems === 0 ? "Tu carrito está vacío" : `${totalItems} artículo${totalItems !== 1 ? 's' : ''} en tu carrito`}
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col flex-1 pt-6 min-h-0">
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground font-body">Tu carrito está vacío</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto pr-2 min-h-0">
                <div className="space-y-4">
                  {items.map((item) => (
                    <div key={item.variantId} className="flex gap-4 p-3 rounded-xl bg-muted/50">
                      <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                        {item.product.node.images?.edges?.[0]?.node && (
                          <img src={item.product.node.images.edges[0].node.url} alt={getDisplayTitle(item.product.node.handle, item.product.node.title)} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-display font-medium truncate">{getDisplayTitle(item.product.node.handle, item.product.node.title)}</h4>
                        {(() => {
                          const opts = item.selectedOptions.filter(
                            o => o.value && o.value.toLowerCase() !== 'default title'
                          );
                          return opts.length > 0 ? (
                            <p className="text-sm text-muted-foreground">{opts.map(o => o.value).join(' • ')}</p>
                          ) : null;
                        })()}
                        <p className="font-semibold text-primary">${parseFloat(item.price.amount).toFixed(2)} {item.price.currencyCode}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(item.variantId)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                        <div className="flex items-center gap-1">
                          <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => updateQuantity(item.variantId, item.quantity - 1)}>
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center text-sm">{item.quantity}</span>
                          <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => updateQuantity(item.variantId, item.quantity + 1)}>
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0 space-y-4 pt-4 border-t">
                <div
                  className={`rounded-xl p-4 space-y-2.5 transition-colors duration-500 ${
                    hasFreeShipping
                      ? "bg-primary/10 border border-primary/30"
                      : "bg-muted/50"
                  }`}
                >
                  {hasFreeShipping ? (
                    <div className="flex items-center gap-2 text-sm font-semibold text-primary animate-in fade-in zoom-in-95 duration-500">
                      <Sparkles className="h-4 w-4 flex-shrink-0" />
                      <span>¡Desbloqueaste envío GRATIS! Ahorras ${SHIPPING_COST} MXN 🎉</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-foreground">
                      <Truck className="h-4 w-4 flex-shrink-0 text-primary" />
                      <span>
                        Te faltan <span className="font-bold text-primary">${remainingForFreeShipping.toFixed(0)} MXN</span> para envío <span className="font-semibold">gratis</span> 🚚
                      </span>
                    </div>
                  )}
                  <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full transition-all duration-700 ease-out ${
                        hasFreeShipping ? "bg-primary" : "bg-gradient-to-r from-primary/70 to-primary"
                      }`}
                      style={{ width: `${freeShippingProgress}%` }}
                    />
                  </div>
                  {!hasFreeShipping && (
                    <p className="text-xs text-muted-foreground">
                      Envío estándar: <span className="font-medium">${SHIPPING_COST} MXN</span> · Gratis en compras +${FREE_SHIPPING_THRESHOLD.toLocaleString('es-MX')}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center text-sm text-muted-foreground">
                    <span>Subtotal</span>
                    <span>${totalPrice.toFixed(2)} {items[0]?.price.currencyCode}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Envío</span>
                    {hasFreeShipping ? (
                      <span className="font-semibold text-primary">GRATIS</span>
                    ) : (
                      <span className="text-muted-foreground">${SHIPPING_COST}.00 MXN</span>
                    )}
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className="text-lg font-display font-semibold">Total estimado</span>
                    <span className="text-xl font-bold text-primary">
                      ${(totalPrice + (hasFreeShipping ? 0 : SHIPPING_COST)).toFixed(2)} {items[0]?.price.currencyCode}
                    </span>
                  </div>
                </div>
                <Button onClick={handleCheckout} className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full font-display" size="lg" disabled={items.length === 0 || isLoading || isSyncing}>
                  {isLoading || isSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4 mr-2" />Pagar con Shopify</>}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
